import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../services/accountingApi';
import Pagination from '../../../components/shared/Pagination';

function VouchersIndex() {
    const [vouchers, setVouchers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState<{[key: string]: boolean}>({});
    const [error, setError] = useState<string | null>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ 
        total_pages: 1, 
        total_count: 0, 
        current_page: 1,
        per_page: 25 
    });

    // Filter state
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        fromDate: '',
        toDate: '',
        status: ''
    });

    useEffect(() => {
        loadVouchers();

        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [currentPage, filters]);

    const loadVouchers = async () => {
        try {
            setError(null);
            setLoading(true);
            
            const params: any = { 
                page: currentPage,
                q: filters.search,
                from_date: filters.fromDate,
                to_date: filters.toDate,
                status: filters.status
            };

            const response = await accountingApi.getVouchers(params);
            if (response.success) {
                const vouchersData = Array.isArray(response.data) ? response.data : (response.data?.vouchers || response.data?.data || []);
                setVouchers(vouchersData);
                if (response.meta) {
                    setPagination({
                        total_pages: response.meta.total_pages || 1,
                        total_count: response.meta.total_count || 0,
                        current_page: response.meta.current_page || 1,
                        per_page: response.meta.per_page || 25
                    });
                }
            } else {
                setError(response.message || 'Failed to load vouchers');
            }
        } catch (err: any) {
            console.error('Error loading vouchers:', err);
            setError('Failed to load vouchers');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field: string, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleClearFilters = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setFilters({ search: '', fromDate: '', toDate: '', status: '' });
        setCurrentPage(1);
    };

    const handlePost = async (voucherId: any) => {
        try {
            setPosting(prev => ({ ...prev, [voucherId]: true }));
            const response = await accountingApi.postVoucher(voucherId);
            if (response.success) {
                loadVouchers();
            } else {
                alert(response.message || 'Failed to post voucher');
            }
        } catch (error) {
            alert('Failed to post voucher');
        } finally {
            setPosting(prev => ({ ...prev, [voucherId]: false }));
        }
    };

    const handleUnpost = async (voucherId: any) => {
        try {
            setPosting(prev => ({ ...prev, [voucherId]: true }));
            const response = await accountingApi.unpostVoucher(voucherId);
            if (response.success) {
                loadVouchers();
            } else {
                alert(response.message || 'Failed to unpost voucher');
            }
        } catch (error) {
            alert('Failed to unpost voucher');
        } finally {
            setPosting(prev => ({ ...prev, [voucherId]: false }));
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const activeFilterCount = [filters.search, filters.fromDate, filters.toDate, filters.status].filter(Boolean).length;

    if (loading && vouchers.length === 0) return <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-slate-200 m-8">Loading...</div>;

    if (error) return (
        <div className="p-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center shadow-sm">
                <p className="text-rose-600 font-bold mb-4">{error}</p>
                <button 
                    type="button" 
                    onClick={loadVouchers} 
                    className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20"
                >
                    Retry
                </button>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 pb-16">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Vouchers</h1>
                    <p className="text-slate-500 mt-1">Journal entries and transactions ({pagination.total_count} found)</p>
                </div>
                <Link to="/accounting/vouchers/new"
                    className="inline-flex items-center justify-center px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-600/20">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
                    New Voucher
                </Link>
            </div>

            {/* Filter Bar */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-8 overflow-hidden">
                <div
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center justify-between px-6 py-4 cursor-pointer transition-colors ${showFilters ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                >
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/></svg>
                        <span className="font-bold text-slate-700 text-sm uppercase tracking-wider">Filters</span>
                        {activeFilterCount > 0 && (
                            <span className="bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                {activeFilterCount}
                            </span>
                        )}
                    </div>
                    <svg className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/></svg>
                </div>

                {showFilters && (
                    <div className="p-6 border-t border-slate-100 animate-in slide-in-from-top duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Search</label>
                                <input
                                    type="text"
                                    placeholder="Voucher No, Narration..."
                                    value={filters.search}
                                    onChange={(e) => handleFilterChange('search', e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm transition-all"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">From Date</label>
                                <input
                                    type="date"
                                    value={filters.fromDate}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm transition-all text-slate-600"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">To Date</label>
                                <input
                                    type="date"
                                    value={filters.toDate}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm transition-all text-slate-600"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Status</label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none text-sm transition-all text-slate-600"
                                >
                                    <option value="">All Status</option>
                                    <option value="posted">Posted</option>
                                    <option value="draft">Draft</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex justify-end mt-6">
                            <button 
                                type="button" 
                                onClick={handleClearFilters}
                                className="px-6 py-2 text-sm font-bold text-slate-500 hover:text-indigo-600 transition-colors uppercase tracking-widest"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Cards View */}
            {isMobile && (
                <div className="grid grid-cols-1 gap-4">
                    {vouchers.length > 0 ? vouchers.map((voucher: any) => (
                        <div key={voucher.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:border-indigo-200 transition-all">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <p className="font-mono font-bold text-indigo-600 mb-1">{voucher.voucher_number}</p>
                                    <p className="text-xs font-medium text-slate-400">{new Date(voucher.voucher_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                                </div>
                                <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider border ${voucher.is_posted ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                                    {voucher.is_posted ? 'Posted' : 'Draft'}
                                </span>
                            </div>

                            <div className="flex items-center gap-3 mb-4">
                                <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border border-indigo-100">
                                    {voucher.voucher_type?.name || voucher.voucher_type || '-'}
                                </span>
                                <span className="text-lg font-mono font-bold text-slate-900 tracking-tighter">
                                    ₹{parseFloat(voucher.total_amount || voucher.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                </span>
                            </div>

                            {voucher.narration && (
                                <p className="text-xs text-slate-500 mb-5 line-clamp-2 leading-relaxed italic">{voucher.narration}</p>
                            )}

                            <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-50">
                                <Link to={`/accounting/vouchers/${voucher.id}`} className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-[10px] font-bold rounded-lg uppercase tracking-wider hover:bg-indigo-100 transition-colors">Details</Link>
                                {!voucher.is_posted && (
                                    <>
                                        <Link to={`/accounting/vouchers/${voucher.id}/edit`} className="px-3 py-1.5 bg-slate-50 text-slate-600 text-[10px] font-bold rounded-lg uppercase tracking-wider hover:bg-slate-100 transition-colors">Edit</Link>
                                        <button 
                                            type="button" 
                                            onClick={() => handlePost(voucher.id)} 
                                            disabled={posting[voucher.id]}
                                            className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-lg uppercase tracking-wider hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                        >
                                            {posting[voucher.id] ? '...' : 'Post'}
                                        </button>
                                    </>
                                )}
                                {voucher.is_posted && (
                                    <button 
                                        type="button" 
                                        onClick={() => handleUnpost(voucher.id)} 
                                        disabled={posting[voucher.id]}
                                        className="px-3 py-1.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-lg uppercase tracking-wider hover:bg-amber-100 transition-colors disabled:opacity-50"
                                    >
                                        {posting[voucher.id] ? '...' : 'Unpost'}
                                    </button>
                                )}
                            </div>
                        </div>
                    )) : (
                        <div className="bg-white rounded-2xl p-12 text-center text-slate-400 border border-slate-200 border-dashed">
                            No vouchers found.
                        </div>
                    )}
                </div>
            )}

            {/* Desktop Table View */}
            {!isMobile && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                            <tr>
                                <th className="px-6 py-4 text-left">Date</th>
                                <th className="px-6 py-4 text-left">Voucher No</th>
                                <th className="px-6 py-4 text-left">Type</th>
                                <th className="px-6 py-4 text-left">Description</th>
                                <th className="px-6 py-4 text-right">Amount</th>
                                <th className="px-6 py-4 text-center">Status</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {vouchers.length > 0 ? vouchers.map((voucher: any) => (
                                <tr key={voucher.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-6 py-4 text-slate-500 whitespace-nowrap">{new Date(voucher.voucher_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                    <td className="px-6 py-4 font-mono font-bold text-indigo-600 whitespace-nowrap underline decoration-indigo-200 decoration-2 underline-offset-4 group-hover:decoration-indigo-500 transition-all">{voucher.voucher_number}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border border-indigo-100">
                                            {voucher.voucher_type?.name || voucher.voucher_type || '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-500 max-w-[250px] truncate italic">{voucher.narration || '-'}</td>
                                    <td className="px-6 py-4 text-right font-mono font-bold text-slate-900 whitespace-nowrap tracking-tighter">₹{parseFloat(voucher.total_amount || voucher.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`px-2.5 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider border ${voucher.is_posted ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                                            {voucher.is_posted ? 'Posted' : 'Draft'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-4">
                                            <Link to={`/accounting/vouchers/${voucher.id}`} className="text-indigo-600 hover:text-indigo-800 font-bold text-[10px] uppercase tracking-wider">Details</Link>
                                            {!voucher.is_posted && (
                                                <>
                                                    <Link to={`/accounting/vouchers/${voucher.id}/edit`} className="text-slate-500 hover:text-slate-700 font-bold text-[10px] uppercase tracking-wider">Edit</Link>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => handlePost(voucher.id)} 
                                                        disabled={posting[voucher.id]} 
                                                        className="text-emerald-600 hover:text-emerald-800 font-bold text-[10px] uppercase tracking-wider disabled:opacity-50"
                                                    >
                                                        {posting[voucher.id] ? '...' : 'Post'}
                                                    </button>
                                                </>
                                            )}
                                            {voucher.is_posted && (
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleUnpost(voucher.id)} 
                                                    disabled={posting[voucher.id]} 
                                                    className="text-amber-600 hover:text-amber-800 font-bold text-[10px] uppercase tracking-wider disabled:opacity-50"
                                                >
                                                    {posting[voucher.id] ? '...' : 'Unpost'}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-20 text-center text-slate-400 border-dashed border-2 border-slate-50 rounded-b-2xl">
                                        <div className="flex flex-col items-center">
                                            <svg className="w-12 h-12 mb-4 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                            <p className="font-bold text-slate-400 uppercase tracking-widest text-[11px]">No vouchers found matches your filters</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalCount={pagination.total_count}
                perPage={pagination.per_page}
                onPageChange={handlePageChange}
            />
        </div>
    );
}

export default VouchersIndex;
